import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { AMENITY_CONSTANTS } from "@constants/amenities";
import { CONSTANTS } from "@constants/index";
import { DateFormats } from "@enums/shared.enum";
import { useBooking } from "@hooks/useBooking/useBooking";
import { useToast } from "@hooks/useToast";
import { UseAddAmenitiesProps } from "@models/components/amenity.model";
import {
  AmenityAPIResponse,
  AmenityTranslation,
} from "@models/services/amenity";
import { SelectedAmenity } from "@models/services/booking";
import { LocalizationObjectType } from "@models/utils/localisation.models";
import { applyLocalization } from "@services/common.service";
import API_SERVICES from "@services/index";
import bookingSlice from "@slices/booking.slice";
import { RootState } from "@store/index";
import {
  checkIsAmenityDisabled,
  getPriceInfo,
  isAmenityApplicable,
} from "@utils/amenity.utils";

const encounteredAmenitiesCache: Record<string, AmenityAPIResponse> = {};

export const useAddAmenities = ({
  amenitiesList,
  locationId,
  selectedAmenitie,
  selectedDate,
  selectedSlot,
  selectedTime,
  setAmenitiesList,
  setSelectedAmenities,
}: UseAddAmenitiesProps) => {
  const { i18n, t } = useTranslation();
  const currentLang = (i18n.language?.split("-")[CONSTANTS.DEFAULT_VALUE] ||
    CONSTANTS.ENGLISH_LANGUAGE) as keyof AmenityTranslation;
  const dispatch = useDispatch();

  const {
    amenityPriceChangedError,
    amenityUnavailableError,
    bookingEditData,
    isEditBooking,
  } = useSelector((state: RootState) => state.booking);

  const { fetchAmenities, resourcePrices } = useBooking();
  const [open, setOpen] = useState("");
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [selectedInfoAmenity, setSelectedInfoAmenity] =
    useState<AmenityAPIResponse | null>(null);
  const [isAmenitiesLoading, setIsAmenitiesLoading] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<{
    id: string;
    isBooked?: boolean;
    uuid?: string;
    title: string;
  } | null>(null);
  const [isNextAvailabilityPopoverOpen, setIsNextAvailabilityPopoverOpen] =
    useState(false);
  const [isNextAvailabilityLoading, setIsNextAvailabilityLoading] =
    useState(false);
  const [nextAvailabilityData, setNextAvailabilityData] = useState<{
    amenityTitle: string;
    nextAvailableDatetime: string | null;
    noAvailability: boolean;
  } | null>(null);
  const [nextAvailabilityTarget, setNextAvailabilityTarget] = useState<
    HTMLElement | string | null
  >(null);
  const nextAvailabilityRequestIdRef = useRef(0);
  const nextAvailabilityAbortControllerRef = useRef<AbortController | null>(
    null
  );

  const handleCheckNextAvailability = async (
    id: string,
    title?: LocalizationObjectType | string,
    target?: HTMLElement | string,
    quantity?: number
  ) => {
    const effectiveLocationId =
      locationId || bookingEditData?.location?.id || "";
    const effectiveDate = selectedDate || bookingEditData?.startDate || "";
    const effectiveTime = selectedTime || bookingEditData?.startTime || "";
    const effectiveSlot =
      selectedSlot?.slot?.slot || bookingEditData?.timeSlot || 0;

    // Guard: required parameters for availability check must be present and valid
    if (
      !effectiveLocationId?.trim() ||
      !effectiveDate?.trim() ||
      !effectiveTime?.trim() ||
      !effectiveSlot ||
      Number(effectiveSlot) <= 0
    ) {
      handleCloseNextAvailability();
      return;
    }

    const effectiveEndTime =
      selectedSlot?.slot?.ends ||
      (effectiveTime && effectiveSlot
        ? moment(effectiveTime, DateFormats.Time)
            .add(Number(effectiveSlot), "minutes")
            .format(DateFormats.Time)
        : "") ||
      bookingEditData?.endTime ||
      "";

    if (!effectiveEndTime?.trim()) {
      handleCloseNextAvailability();
      return;
    }

    // Resolve requested quantity based on the user's selected/booked quantity, defaulting to 1
    const matchingSelections = selectedAmenitie.filter(
      sa => sa && sa.id === id && !sa.isDeleted
    );
    const selectedQuantity = matchingSelections.reduce(
      (sum, sa) => sum + (Number(sa.count) || 0),
      0
    );
    const bookedAmenity = bookingEditData?.amenities?.find(a => a.id === id);
    const effectiveQuantity =
      quantity && quantity > 0
        ? quantity
        : selectedQuantity > 0
          ? selectedQuantity
          : bookedAmenity?.quantity && bookedAmenity.quantity > 0
            ? bookedAmenity.quantity
            : 1;

    const amenity = amenitiesList.find(a => a.id === id);
    const resolvedTitle =
      (typeof title === "string"
        ? title
        : title
          ? applyLocalization(title)
          : "") ||
      (amenity ? applyLocalization(amenity.title) : "") ||
      "";

    const resolvedTarget =
      target ||
      (typeof document !== "undefined"
        ? document.getElementById(`fg-btn-check-availability-${id}`) ||
          document.getElementById(`fg-btn-check-availability-table-${id}`) ||
          document.getElementById(`fg-btn-check-availability-mobile-${id}`)
        : null) ||
      `fg-btn-check-availability-${id}`;

    // Cancel any previous in-flight request and advance request sequence
    if (nextAvailabilityAbortControllerRef.current) {
      nextAvailabilityAbortControllerRef.current.abort();
    }
    const controller = new AbortController();
    nextAvailabilityAbortControllerRef.current = controller;
    const currentRequestId = ++nextAvailabilityRequestIdRef.current;

    setNextAvailabilityTarget(resolvedTarget);
    setIsNextAvailabilityPopoverOpen(true);
    setIsNextAvailabilityLoading(true);
    setNextAvailabilityData({
      amenityTitle: resolvedTitle,
      nextAvailableDatetime: null,
      noAvailability: false,
    });

    try {
      const res = await API_SERVICES.BOOKING.CHECK_AMENITY_NEXT_AVAILABILITY({
        amenityId: id,
        bookingDate: effectiveDate,
        endTime: effectiveEndTime,
        locationId: effectiveLocationId,
        quantity: effectiveQuantity,
        slotTime: effectiveSlot,
        startTime: effectiveTime,
      });

      // Ignore late response if request was superseded or popover was closed
      if (
        currentRequestId !== nextAvailabilityRequestIdRef.current ||
        controller.signal.aborted
      ) {
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const resData = (res as any)?.data ?? res;
      if (resData?.nextAvailableDatetime) {
        setNextAvailabilityData({
          amenityTitle: resolvedTitle,
          nextAvailableDatetime: resData.nextAvailableDatetime,
          noAvailability: false,
        });
      } else {
        setNextAvailabilityData({
          amenityTitle: resolvedTitle,
          nextAvailableDatetime: null,
          noAvailability: true,
        });
      }
    } catch {
      // Ignore errors from superseded or aborted requests
      if (
        currentRequestId !== nextAvailabilityRequestIdRef.current ||
        controller.signal.aborted
      ) {
        return;
      }

      setNextAvailabilityData({
        amenityTitle: resolvedTitle,
        nextAvailableDatetime: null,
        noAvailability: true,
      });
    } finally {
      // Only the active request should reset loading state
      if (
        currentRequestId === nextAvailabilityRequestIdRef.current &&
        !controller.signal.aborted
      ) {
        setIsNextAvailabilityLoading(false);
      }
    }
  };

  const handleCloseNextAvailability = useCallback(() => {
    nextAvailabilityRequestIdRef.current += 1;
    if (nextAvailabilityAbortControllerRef.current) {
      nextAvailabilityAbortControllerRef.current.abort();
      nextAvailabilityAbortControllerRef.current = null;
    }
    setIsNextAvailabilityPopoverOpen(false);
    setIsNextAvailabilityLoading(false);
    setNextAvailabilityTarget(null);
  }, []);

  useEffect(() => {
    return () => {
      nextAvailabilityRequestIdRef.current += 1;
      if (nextAvailabilityAbortControllerRef.current) {
        nextAvailabilityAbortControllerRef.current.abort();
        nextAvailabilityAbortControllerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    handleCloseNextAvailability();
  }, [
    selectedSlot?.resourceId,
    selectedSlot?.slot?.slot,
    selectedDate,
    selectedTime,
    locationId,
    handleCloseNextAvailability,
  ]);
  const { CustomToast } = useToast();

  const isBookingParamsChanged = useMemo(() => {
    if (!isEditBooking || !bookingEditData) return false;

    const dateChanged = selectedDate !== bookingEditData.startDate;
    const timeChanged = selectedTime !== bookingEditData.startTime;
    const locationChanged = locationId !== bookingEditData.location?.id;
    const resourceChanged =
      selectedSlot?.resourceId !== bookingEditData.resource?.id;
    const durationChanged =
      selectedSlot?.slot?.slot?.toString() !==
      bookingEditData.timeSlot?.toString();

    return (
      dateChanged ||
      timeChanged ||
      locationChanged ||
      resourceChanged ||
      durationChanged
    );
  }, [
    isEditBooking,
    bookingEditData,
    selectedDate,
    selectedTime,
    locationId,
    selectedSlot,
  ]);

  // Track all amenities encountered during this session to get names for unavailable selections
  const encounteredAmenitiesRef = useRef<Record<string, AmenityAPIResponse>>(
    {}
  );

  useEffect(() => {
    amenitiesList.forEach(a => {
      encounteredAmenitiesRef.current[a.id] = a;
      encounteredAmenitiesCache[a.id] = a;
    });
  }, [amenitiesList]);

  const handleMandatorySelection = (data: AmenityAPIResponse[]) => {
    const mandatory = data.filter(
      amenity =>
        amenity.isMandatory &&
        !checkIsAmenityDisabled(
          amenity,
          selectedSlot,
          selectedTime,
          selectedDate
        ) &&
        calculateMaxQty(amenity) > 0 &&
        !selectedAmenitie.some(
          selectedAmenity =>
            selectedAmenity && selectedAmenity.id === amenity.id
        )
    );
    if (mandatory.length > AMENITY_CONSTANTS.DEFAULT_AMENITY_MANDATORY_COUNT) {
      const resourceId = selectedSlot?.resourceId || "";
      const newSels: SelectedAmenity[] = mandatory.map(mandatoryAmenity => ({
        count: AMENITY_CONSTANTS.DEFAULT_COUNT,
        id: mandatoryAmenity.id,
        resourceId,
        uuid: Math.random().toString(36).substring(2, 9),
      }));
      setSelectedAmenities([...selectedAmenitie, ...newSels]);
    }
  };

  useEffect(() => {
    if (
      locationId &&
      selectedDate &&
      selectedTime &&
      selectedSlot?.slot?.slot
    ) {
      setIsAmenitiesLoading(true);
      fetchAmenities({
        bookingDate: selectedDate,
        bookingId: isEditBooking ? bookingEditData?.bookingId : undefined,
        locationId,
        resourceId: selectedSlot?.resourceId,
        slotTime: selectedSlot?.slot?.slot,
        startTime: selectedTime,
      }).then((data: AmenityAPIResponse[] | null) => {
        setIsAmenitiesLoading(false);
        if (data) {
          setAmenitiesList(data);
          handleMandatorySelection(data);
        }
      });
    }
  }, [
    locationId,
    selectedDate,
    selectedTime,
    selectedSlot?.resourceId,
    selectedSlot?.slot?.slot,
    isEditBooking,
    bookingEditData?.bookingId,
  ]);

  const toggle = (id: string) => {
    if (open === id) {
      setOpen("");
    } else {
      setOpen(id);
    }
  };

  const handleAdd = (id: string) => {
    const resourceId = selectedSlot?.resourceId || "";
    const newSelection: SelectedAmenity = {
      count: AMENITY_CONSTANTS.DEFAULT_COUNT,
      id,
      resourceId,
      uuid: Math.random().toString(36).substring(2, 9),
    };
    setSelectedAmenities([...selectedAmenitie, newSelection]);
    if (isEditBooking) {
      dispatch(bookingSlice.actions.setIsAmenityEdit(true));
      dispatch(bookingSlice.actions.setBookingDataChanged(true));
    }
  };

  const ensureMandatoryAmenities = (
    amenities: SelectedAmenity[],
    removedId: string
  ) => {
    const amenity = amenitiesList.find(amenitie => amenitie.id === removedId);
    const isDisabled =
      amenity &&
      checkIsAmenityDisabled(amenity, selectedSlot, selectedTime, selectedDate);

    if (amenity?.isMandatory && !isDisabled && calculateMaxQty(amenity) > 0) {
      const hasAny = amenities.some(
        selectedAmenity => selectedAmenity && selectedAmenity.id === removedId
      );
      if (!hasAny) {
        const originalSelection = selectedAmenitie.find(
          sa => sa && sa.id === removedId
        );
        if (originalSelection && !originalSelection.isBooked) {
          const maxQtyForAmenity = calculateMaxQty(amenity);
          const validCount =
            originalSelection.count > maxQtyForAmenity
              ? AMENITY_CONSTANTS.DEFAULT_COUNT
              : originalSelection.count;

          amenities.push({
            ...originalSelection,
            count: validCount || AMENITY_CONSTANTS.DEFAULT_COUNT,
          });
        } else {
          amenities.push({
            count: AMENITY_CONSTANTS.DEFAULT_COUNT,
            id: removedId,
            resourceId: selectedSlot?.resourceId || "",
            uuid: Math.random().toString(36).substring(2, 9),
          });
        }
      }
    }
    return amenities;
  };

  const handleRemove = (id: string, isBooked?: boolean, uuid?: string) => {
    if (isBooked) {
      const currentAmenity = amenitiesList.find(a => a.id === id);
      const bookedDataSelection = selectedAmenitie.find(sa =>
        uuid ? sa.uuid === uuid : sa.id === id && sa.isBooked
      );
      const bookedDataInEdit = bookingEditData?.amenities?.find(a =>
        bookedDataSelection?.bookingAmenityId
          ? a.bookingAmenityId === bookedDataSelection.bookingAmenityId
          : a.id === id
      );

      const currentAmenityTitle =
        currentAmenity?.title && applyLocalization(currentAmenity?.title);
      const bookedDataAmenityTitle =
        bookedDataInEdit?.title && applyLocalization(bookedDataInEdit?.title);
      const title = currentAmenityTitle || bookedDataAmenityTitle || "";

      setConfirmTarget({ id, isBooked, title, uuid });
      setIsConfirmOpen(true);
    } else {
      let updatedAmenities = selectedAmenitie.filter(sa => {
        if (!sa) return false;
        if (uuid) return sa.uuid !== uuid;
        if (isBooked !== undefined)
          return !(sa.id === id && sa.isBooked === isBooked);
        return sa.id !== id;
      });

      updatedAmenities = ensureMandatoryAmenities(updatedAmenities, id);
      setSelectedAmenities(updatedAmenities);
      if (isEditBooking) {
        dispatch(bookingSlice.actions.setIsAmenityEdit(true));
        dispatch(bookingSlice.actions.setBookingDataChanged(true));
      }
    }
  };

  const confirmRemove = () => {
    if (!confirmTarget) return;
    const { id, isBooked, uuid } = confirmTarget;

    let updatedAmenities = selectedAmenitie.filter(sa => {
      if (!sa) return false;
      if (uuid) return sa.uuid !== uuid;
      if (isBooked !== undefined)
        return !(sa.id === id && sa.isBooked === isBooked);
      return sa.id !== id;
    });

    updatedAmenities = ensureMandatoryAmenities(updatedAmenities, id);
    setSelectedAmenities(updatedAmenities);
    if (isEditBooking) {
      dispatch(bookingSlice.actions.setIsAmenityEdit(true));
      dispatch(bookingSlice.actions.setBookingDataChanged(true));
    }

    setIsConfirmOpen(false);
    setConfirmTarget(null);
  };

  const handleCloseConfirm = () => {
    setIsConfirmOpen(false);
    setConfirmTarget(null);
  };

  const handleQuantityChange = (
    id: string,
    newQuantity: number,
    isBooked?: boolean,
    uuid?: string
  ) => {
    const currentAmenity = amenitiesList.find(amenitie => amenitie.id === id);
    let maxQty = newQuantity;
    if (currentAmenity) {
      if (isBooked) {
        const availablePool =
          currentAmenity.bookableCount?.availableCount ??
          AMENITY_CONSTANTS.PER_AMENITY;

        const limitPerBooking =
          currentAmenity.allowMultipleQuantity > 0
            ? currentAmenity.allowMultipleQuantity
            : AMENITY_CONSTANTS.PER_AMENITY;

        maxQty = isBookingParamsChanged
          ? Math.min(limitPerBooking, availablePool)
          : Math.min(limitPerBooking, availablePool + newQuantity);
      } else {
        const bookedQuantity = selectedAmenitie
          .filter(sa => sa.id === id && sa.isBooked)
          .reduce((sum, sa) => sum + sa.count, 0);
        maxQty = Math.max(0, calculateMaxQty(currentAmenity) - bookedQuantity);
      }
    } else if (isBooked) {
      const sa = selectedAmenitie.find(s => s && s.id === id && s.isBooked);
      maxQty = sa ? sa.count : newQuantity;
    }
    const cappedQuantity = Math.min(newQuantity, maxQty);

    let found = false;
    const updatedAmenities = selectedAmenitie.map(sa => {
      if (!sa) return sa;
      let matches = false;
      if (uuid) {
        matches = sa.uuid === uuid;
      } else if (typeof isBooked === "boolean") {
        matches = sa.id === id && sa.isBooked === isBooked;
      } else {
        matches = sa.id === id;
      }
      if (matches) {
        found = true;
        return { ...sa, count: cappedQuantity };
      }
      return sa;
    });

    if (!found && !isBooked && cappedQuantity > 0) {
      const resourceId = selectedSlot?.resourceId || "";
      const newSelection: SelectedAmenity = {
        count: cappedQuantity,
        id,
        resourceId,
        uuid: Math.random().toString(36).substring(2, 9),
      };
      updatedAmenities.push(newSelection);
    }

    let finalAmenities = updatedAmenities.filter(
      sa => sa && (sa.count > 0 || sa.isBooked)
    );

    finalAmenities = ensureMandatoryAmenities(finalAmenities, id);

    setSelectedAmenities(finalAmenities);
  };

  const handleInfoToggle = (amenity: AmenityAPIResponse | null = null) => {
    setSelectedInfoAmenity(amenity);
    setIsInfoOpen(!!amenity);
  };

  const calculateMaxQty = (amenity: AmenityAPIResponse | undefined | null) => {
    if (!amenity) return 0;
    const limitPerBooking =
      amenity.allowMultipleQuantity > 0 ? amenity.allowMultipleQuantity : 0;

    return Math.min(amenity?.bookableCount?.availableCount, limitPerBooking);
  };

  const getAmenityPriceInfo = (amenity: AmenityAPIResponse) => {
    const resourceId = selectedSlot?.resourceId;
    const duration = selectedSlot?.slot?.slot;
    if (!resourceId || !duration) return AMENITY_CONSTANTS.DEFAULT_PRICE_INFO;

    const resourceData = amenity.resources?.[resourceId];
    if (!resourceData) return AMENITY_CONSTANTS.DEFAULT_PRICE_INFO;

    const priceInfo = getPriceInfo(resourceData.prices, duration);

    if (!priceInfo) return AMENITY_CONSTANTS.DEFAULT_PRICE_INFO;

    return {
      isVatInclusive:
        resourceData.vat?.vatHandlingType === AMENITY_CONSTANTS.VAT_INCLUSIVE,
      price: priceInfo?.totalPrice ?? AMENITY_CONSTANTS.DEFAULT_PRICE,
      vat: resourceData.vat?.vatPercentage ?? AMENITY_CONSTANTS.DEFAULT_VAT,
    };
  };

  const availableAmenities = useMemo(() => {
    // Current available ones from API
    const list = amenitiesList.filter(amenity => {
      const isDisabled = checkIsAmenityDisabled(
        amenity,
        selectedSlot,
        selectedTime,
        selectedDate
      );
      return !isDisabled;
    });

    // Plus any selected ones that are NOT in the list (now unavailable for this duration)
    selectedAmenitie.forEach(sa => {
      if (!sa || sa.isBooked) return;
      const exists = list.some(a => a.id === sa.id);
      if (!exists) {
        const encountered = encounteredAmenitiesRef.current[sa.id];
        if (encountered) {
          list.push(encountered);
        } else if (
          sa.bookingAmenityId &&
          isEditBooking &&
          bookingEditData?.amenities
        ) {
          // Amenity was saved in the booking (has bookingAmenityId) but is no longer
          // returned by the current API. Synthesize from bookingEditData so it
          // still appears in "Available to Add" with a remove button.
          const bookedAmenity = bookingEditData.amenities.find(
            a => a.bookingAmenityId === sa.bookingAmenityId || a.id === sa.id
          );
          if (bookedAmenity) {
            const synthetic: AmenityAPIResponse = {
              allowMultipleQuantity: bookedAmenity.quantity || 1,
              bookableCount: { availableCount: 0, bookingId: [] },
              capacityManagement: {
                capacityCount: bookedAmenity.quantity || 1,
                capacityType: "SINGLE",
                capacityUnit: null,
              },
              description: { default: "", locales: {} },
              id: bookedAmenity.id,
              isMandatory: false,
              resources: {},
              title: bookedAmenity.title,
            };
            list.push(synthetic);
            encounteredAmenitiesRef.current[synthetic.id] = synthetic;
          }
        }
      }
    });

    return list;
  }, [
    amenitiesList,
    selectedAmenitie,
    selectedSlot,
    selectedTime,
    selectedDate,
    isEditBooking,
    bookingEditData,
  ]);

  const {
    unavailableAddedAmenityIds,
    unavailableAddedAmenityNames,
    unavailableBookedAmenityIds,
    unavailableBookedAmenityNames,
  } = useMemo(() => {
    const bookedNames: string[] = [];
    const addedNames: string[] = [];
    const bookedIds: string[] = [];
    const addedIds: string[] = [];

    selectedAmenitie.forEach(sa => {
      if (!sa) return;
      const currentAmenity = amenitiesList.find(va => va.id === sa.id);

      let isQuantityNotAvailable = false;
      if (currentAmenity) {
        if (sa.isBooked) {
          const availableCount =
            currentAmenity.bookableCount?.availableCount ??
            currentAmenity.capacityManagement?.capacityCount ??
            0;
          const capacityCount =
            currentAmenity.capacityManagement?.capacityCount ?? 0;

          const limitPerBooking =
            currentAmenity.allowMultipleQuantity > 0
              ? currentAmenity.allowMultipleQuantity
              : capacityCount;
          const maxQty = isBookingParamsChanged
            ? Math.min(limitPerBooking, availableCount)
            : Math.min(limitPerBooking, availableCount + sa.count);
          if (sa.count > maxQty) {
            isQuantityNotAvailable = true;
          }
        } else {
          const bookedQuantity = selectedAmenitie
            .filter(s => s.id === sa.id && s.isBooked)
            .reduce((sum, s) => sum + s.count, 0);
          const maxQty = Math.max(
            0,
            calculateMaxQty(currentAmenity) - bookedQuantity
          );
          if (sa.count > maxQty) {
            isQuantityNotAvailable = true;
          }
        }
      } else {
        isQuantityNotAvailable = true;
      }

      const isVisible =
        currentAmenity &&
        !checkIsAmenityDisabled(
          currentAmenity,
          selectedSlot,
          selectedTime,
          selectedDate
        );
      if (!isVisible || isQuantityNotAvailable) {
        if (sa.isBooked && bookingEditData?.amenities) {
          const originalAmenity = bookingEditData.amenities.find(a =>
            sa.bookingAmenityId
              ? a.bookingAmenityId === sa.bookingAmenityId
              : a.id === sa.id
          );
          if (originalAmenity) {
            const titleObj = originalAmenity.title;
            const locales = titleObj?.locales as Record<string, string>;
            const name =
              locales?.[currentLang] ||
              (typeof titleObj?.default === "string" ? titleObj.default : "") ||
              "";
            bookedNames.push(name);
            bookedIds.push(sa.id);
          }
        } else {
          const encountered =
            encounteredAmenitiesCache[sa.id] ||
            encounteredAmenitiesRef.current[sa.id];
          if (encountered) {
            addedNames.push(applyLocalization(encountered.title));
            addedIds.push(sa.id);
          }
        }
      }
    });
    return {
      unavailableAddedAmenityIds: Array.from(new Set(addedIds)),
      unavailableAddedAmenityNames: Array.from(new Set(addedNames)),
      unavailableBookedAmenityIds: Array.from(new Set(bookedIds)),
      unavailableBookedAmenityNames: Array.from(new Set(bookedNames)),
    };
  }, [
    isEditBooking,
    bookingEditData,
    selectedAmenitie,
    amenitiesList,
    selectedSlot,
    selectedTime,
    currentLang,
  ]);

  const changedAmenityNames = useMemo(() => {
    if (!isEditBooking || !bookingEditData?.amenities) return [];
    const changedNames: string[] = [];
    selectedAmenitie.forEach(sa => {
      if (sa && sa.isBooked) {
        const currentAmenity = amenitiesList.find(a => a.id === sa.id);
        if (currentAmenity) {
          const { price } = getAmenityPriceInfo(currentAmenity);
          const original = bookingEditData?.amenities?.find(a =>
            sa.bookingAmenityId
              ? a.bookingAmenityId === sa.bookingAmenityId
              : a.id === sa.id
          );
          if (original) {
            const originalUnitPrice =
              original.quantity > 0 ? original.unitPrice : 0;
            if (originalUnitPrice !== price) {
              const name = applyLocalization(currentAmenity.title);
              const isUnavailable = unavailableBookedAmenityIds.includes(sa.id);
              if (!isUnavailable) {
                changedNames.push(name);
              }
            }
          }
        }
      }
    });
    return Array.from(new Set(changedNames));
  }, [
    isEditBooking,
    bookingEditData,
    selectedAmenitie,
    amenitiesList,
    selectedSlot,
    currentLang,
    unavailableBookedAmenityIds,
  ]);

  useEffect(() => {
    let hasPriceChanged = changedAmenityNames.length > 0;

    if (
      !hasPriceChanged &&
      isEditBooking &&
      bookingEditData?.amenities &&
      availableAmenities?.length
    ) {
      const isAnyPriceChanged = bookingEditData.amenities.some(
        bookedAmenity => {
          // Only check price changes for booked amenities that are still selected
          const isStillSelected = selectedAmenitie.some(
            sa =>
              sa && sa.id === bookedAmenity.id && sa.isBooked && sa.count > 0
          );
          if (!isStillSelected) return false;

          const currentAmenity = availableAmenities.find(
            a => a.id === bookedAmenity.id
          );
          if (currentAmenity) {
            const { price } = getAmenityPriceInfo(currentAmenity);
            const originalUnitPrice =
              bookedAmenity.quantity > 0 ? bookedAmenity.unitPrice : 0;
            return Number(originalUnitPrice) !== Number(price);
          }
          return false;
        }
      );
      if (isAnyPriceChanged) {
        hasPriceChanged = true;
      }
    }

    dispatch(bookingSlice.actions.setAmenityPriceChangedError(hasPriceChanged));
    if (hasPriceChanged) {
      dispatch(bookingSlice.actions.setBookingDataChanged(true));
    }
  }, [
    changedAmenityNames,
    isEditBooking,
    bookingEditData,
    availableAmenities,
    selectedSlot,
    selectedAmenitie,
  ]);

  useEffect(() => {
    dispatch(
      bookingSlice.actions.setAmenityUnavailableError(
        unavailableBookedAmenityNames.length > 0
      )
    );
  }, [unavailableBookedAmenityNames]);
  useEffect(() => {
    dispatch(
      bookingSlice.actions.setAmenityAddedUnavailableError(
        unavailableAddedAmenityNames.length > 0
      )
    );
  }, [unavailableAddedAmenityNames]);
  const amenityAddedUnavailableError = unavailableAddedAmenityNames.length > 0;

  const unavailableMandatoryAmenityIdsAndNames = useMemo(() => {
    const names: string[] = [];
    const ids: string[] = [];

    amenitiesList.forEach(amenity => {
      if (amenity.isMandatory && isAmenityApplicable(amenity, selectedSlot)) {
        const isDisabled = checkIsAmenityDisabled(
          amenity,
          selectedSlot,
          selectedTime,
          selectedDate
        );
        const maxQty = calculateMaxQty(amenity);

        if (isEditBooking) {
          const originalAmenity = bookingEditData?.amenities?.find(
            a => a.id === amenity.id
          );
          const isAlreadyPaid = originalAmenity?.isPaid === true;

          if (isAlreadyPaid && !isBookingParamsChanged) {
            return;
          }
        }

        if (!isDisabled && maxQty <= 0) {
          names.push(applyLocalization(amenity.title));
          ids.push(amenity.id);
        }
      }
    });
    return {
      ids: Array.from(new Set(ids)),
      names: Array.from(new Set(names)),
    };
  }, [
    amenitiesList,
    selectedSlot,
    selectedTime,
    selectedDate,
    selectedAmenitie,
    isBookingParamsChanged,
    bookingEditData,
    isEditBooking,
  ]);

  const unavailableMandatoryAmenityNames =
    unavailableMandatoryAmenityIdsAndNames.names;
  const unavailableMandatoryAmenityIds =
    unavailableMandatoryAmenityIdsAndNames.ids;

  const activeSelectionsCount = availableAmenities.filter(amenity => {
    const isSelected = selectedAmenitie.some(sa => sa.id === amenity.id);
    return (isSelected && !amenity.isMandatory) || amenity.isMandatory;
  }).length;

  const bookedDataSelections = useMemo(
    () => selectedAmenitie.filter(sa => sa && sa.isBooked),
    [selectedAmenitie]
  );

  const hasConflict = false;

  const handleInfo = (id: string) => {
    const currentAmenity = amenitiesList.find(a => a.id === id);
    handleInfoToggle(currentAmenity || null);
  };

  const handleCloseInfo = () => {
    handleInfoToggle(null);
  };

  const getBookedCardProps = (selectedAmenity: SelectedAmenity) => {
    const bookedData = bookingEditData?.amenities?.find(a =>
      selectedAmenity.bookingAmenityId
        ? a.bookingAmenityId === selectedAmenity.bookingAmenityId
        : a.id === selectedAmenity.id
    );
    const originalUnitPrice =
      bookedData && bookedData.quantity > 0 ? bookedData?.unitPrice : 0;

    const currentAmenity = amenitiesList.find(a => a.id === selectedAmenity.id);

    const currentPriceInfo = currentAmenity
      ? getAmenityPriceInfo(currentAmenity)
      : null;

    const isVatInclusive =
      currentPriceInfo?.isVatInclusive ??
      bookedData?.vatHandlingType === AMENITY_CONSTANTS.VAT_INCLUSIVE;

    const vat = currentPriceInfo?.vat ?? bookedData?.vatPercentage ?? 0;

    let maxQty = selectedAmenity.count;
    if (currentAmenity) {
      const availableCount = currentAmenity.bookableCount?.availableCount ?? 0;

      const limitPerBooking = currentAmenity.allowMultipleQuantity ?? 0;

      maxQty = Math.min(limitPerBooking, availableCount);
    }
    const currentAmenityTitle =
      currentAmenity?.title && applyLocalization(currentAmenity?.title);
    const bookedDataAmenityTitle =
      bookedData?.title && applyLocalization(bookedData?.title);
    const title = currentAmenityTitle || bookedDataAmenityTitle;

    const currentDescription =
      currentAmenity?.description &&
      applyLocalization(currentAmenity.description);
    const bookedDescription =
      bookedData?.description && applyLocalization(bookedData.description);
    const description = currentDescription || bookedDescription || "";

    const key =
      selectedAmenity.uuid ||
      (selectedAmenity.bookingAmenityId
        ? `${selectedAmenity.bookingAmenityId}-original`
        : `${selectedAmenity.id}-original`);

    const isActuallyAvailable =
      !!currentAmenity &&
      !checkIsAmenityDisabled(
        currentAmenity,
        selectedSlot,
        selectedTime,
        selectedDate
      );
    const isUnavailable =
      !isActuallyAvailable || selectedAmenity.count > maxQty;

    return {
      currency: resourcePrices?.currency || "",
      description: description as string,
      error: isUnavailable
        ? t("bookingEdit.amenityUnavailableWarningShort")
        : undefined,
      id: selectedAmenity.id,
      isBooked: true,
      isDisabled: isUnavailable,
      isMandatory: currentAmenity?.isMandatory ?? false,
      isPosted: true,
      isSelected: true,
      isStepperDisabled: true,
      isVatInclusive,
      key,
      maxQuantity: maxQty,
      onAdd: handleAdd,
      onCheckNextAvailability: handleCheckNextAvailability,
      onInfo: handleInfo,
      onQuantityChange: handleQuantityChange,
      onRemove: handleRemove,
      price: originalUnitPrice,
      quantity: selectedAmenity.count,
      title: title as string,
      uuid: selectedAmenity.uuid,
      vatPercentage: Number.parseFloat(vat.toString()),
    };
  };

  const getAvailableCardProps = (amenity: AmenityAPIResponse) => {
    const { isVatInclusive, price, vat } = getAmenityPriceInfo(amenity);
    const bookedQuantity = selectedAmenitie
      .filter(sa => sa.id === amenity.id && sa.isBooked)
      .reduce((sum, sa) => sum + sa.count, 0);
    const maxQty = Math.max(0, calculateMaxQty(amenity) - bookedQuantity);

    const hasOriginal = selectedAmenitie.some(
      sa => sa.id === amenity.id && sa.isBooked
    );

    const amenitySelection = selectedAmenitie.find(
      sa => sa.id === amenity.id && !sa.isBooked
    );

    const isSelected = !!amenitySelection;

    const defaultMandatoryQty = hasOriginal
      ? 0
      : AMENITY_CONSTANTS.DEFAULT_COUNT;

    const quantity =
      amenitySelection?.count ||
      (amenity.isMandatory
        ? defaultMandatoryQty
        : AMENITY_CONSTANTS.DEFAULT_COUNT);

    const showAddMore = selectedAmenitie.some(sa => sa.id === amenity.id);

    let minQuantity: number | undefined;
    if (amenity.isMandatory && !hasOriginal) {
      minQuantity = AMENITY_CONSTANTS.DEFAULT_COUNT;
    }
    const apiAmenity = amenitiesList.find(va => va.id === amenity.id);
    const isActuallyAvailable =
      !!apiAmenity &&
      !checkIsAmenityDisabled(
        apiAmenity,
        selectedSlot,
        selectedTime,
        selectedDate
      ) &&
      calculateMaxQty(apiAmenity) > 0;

    const isSavedBookingAmenity =
      !apiAmenity && !!amenitySelection?.bookingAmenityId;

    const isUnavailable =
      isSelected &&
      (!isActuallyAvailable || quantity > maxQty) &&
      !isSavedBookingAmenity;

    const title = applyLocalization(amenity.title);
    const description = amenity.description
      ? applyLocalization(amenity.description)
      : "";

    const isConflictAlertActive = hasConflict;

    return {
      amenityPriceChangedError: amenityPriceChangedError,
      currency: resourcePrices?.currency || "",
      description,
      error: isUnavailable
        ? t("bookingEdit.amenityUnavailableWarningShort")
        : undefined,
      id: amenity.id,
      isBookingFinanceConfigured: amenity?.isBookingFinanceConfigured,
      isDisabled:
        isUnavailable ||
        maxQty <= 0 ||
        (amenity.isMandatory && !isActuallyAvailable),
      isMandatory: amenity.isMandatory && !hasOriginal,
      isSelected: isSelected || (amenity.isMandatory && !hasOriginal),
      isStepperDisabled: isConflictAlertActive || !apiAmenity,
      isVatInclusive,
      maxQuantity: maxQty,
      minQuantity,
      onAdd: handleAdd,
      onCheckNextAvailability: handleCheckNextAvailability,
      onInfo: handleInfo,
      onQuantityChange: handleQuantityChange,
      onRemove: handleRemove,
      price,
      quantity,
      showAddMore,
      title,
      uuid: amenitySelection?.uuid,
      vatPercentage: Number.parseFloat(vat.toString()),
    };
  };

  useEffect(() => {
    const hasAnyError =
      hasConflict ||
      changedAmenityNames.length > 0 ||
      unavailableAddedAmenityNames.length > 0 ||
      unavailableBookedAmenityNames.length > 0 ||
      unavailableMandatoryAmenityNames.length > 0;

    const hasMandatory = availableAmenities.some(a => a.isMandatory);

    if (hasAnyError || hasMandatory) {
      setOpen(AMENITY_CONSTANTS.OPEN_ACCORDION);
    }
  }, [
    hasConflict,
    changedAmenityNames.length,
    unavailableAddedAmenityNames.length,
    unavailableBookedAmenityNames.length,
    unavailableMandatoryAmenityNames.length,
    amenitiesList,
  ]);

  return {
    activeSelectionsCount,
    amenityAddedUnavailableError,
    amenityPriceChangedError,
    amenityUnavailableError,
    availableAmenities,
    bookedDataSelections,
    bookingEditData,
    calculateMaxQty,
    changedAmenityNames,
    confirmRemove,
    confirmTarget,
    currentLang,
    CustomToast,
    getAmenityPriceInfo,
    getAvailableCardProps,
    getBookedCardProps,
    handleAdd,
    handleCheckNextAvailability,
    handleCloseConfirm,
    handleCloseInfo,
    handleCloseNextAvailability,
    handleInfo,
    handleInfoToggle,
    handleQuantityChange,
    handleRemove,
    hasConflict,
    isAmenitiesLoading,
    isBookingParamsChanged,
    isConfirmOpen,
    isEditBooking,
    isInfoOpen,
    isNextAvailabilityLoading,
    isNextAvailabilityPopoverOpen,
    nextAvailabilityData,
    nextAvailabilityTarget,
    open,
    resourcePrices,
    selectedInfoAmenity,
    t,
    toggle,
    unavailableAddedAmenityIds,
    unavailableAddedAmenityNames,
    unavailableBookedAmenityIds,
    unavailableBookedAmenityNames,
    unavailableMandatoryAmenityIds,
    unavailableMandatoryAmenityNames,
  };
};
